import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { Banknote, Ticket, TrendingUp, Globe2, ShieldPlus, Undo2, BusFront, Vault, Loader2, Radar } from "lucide-react";
import { StatCard } from "@/components/Ui";
import ConsoleHeader from "@/components/ConsoleHeader";
import CashLedger from "@/components/CashLedger";
import { api } from "@/lib/api";
import { money, fmtDate, METHOD_LABEL } from "@/lib/format";

const PERIODS = [[1, "Today"], [7, "7 days"], [30, "30 days"], [90, "90 days"]];
const PIE = ["#16A34A", "#FACC15", "#0052FF"];

const Table = ({ title, rows, cols, testid }) => (
  <div className="card-soft p-5" data-testid={testid}>
    <div className="label-xs mb-3">{title}</div>
    {rows.length ? (
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-slate-100 last:border-0">
              <td className="py-2 font-semibold">{r.name}</td>
              {cols.map(([k, f]) => <td key={k} className="py-2 text-right">{f(r[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    ) : <p className="text-sm text-slate-500">No data for this period.</p>}
  </div>
);

export default function ManagementPage() {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/management/overview", { params: { days } }).then((r) => setD(r.data)); }, [days]);

  return (
    <div className="hero-bg min-h-full">
      <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6" data-testid="management-dashboard">
        <ConsoleHeader role="manager" icon={TrendingUp} title="Financial overview" subtitle="Revenue, tickets, settlements and web analytics." />
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            {d && <p className="text-sm text-slate-500">Showing {fmtDate(d.from)} - {fmtDate(d.to)}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Link to="/control" className="chip btn-press bg-[#182678] py-2 text-white hover:bg-[#0052FF]" data-testid="mgmt-control-room-link"><Radar className="h-4 w-4" />Control room</Link>
          <div className="flex rounded-full border border-slate-200 bg-white p-1">
            {PERIODS.map(([n, l]) => (
              <button key={n} onClick={() => setDays(n)} data-testid={`period-${n}`}
                className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${days === n ? "bg-[#0052FF] text-white" : "text-slate-600 hover:text-[#0052FF]"}`}>{l}</button>
            ))}
          </div>
          </div>
        </div>
        {!d ? <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div> : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard label="Net revenue" value={money(d.net_revenue)} sub={`Gross ${money(d.gross_sales)}`} icon={Banknote} testid="mgmt-net-revenue" />
              <StatCard label="Tickets sold" value={d.tickets.toLocaleString()} icon={Ticket} tint="bg-sky-50 text-sky-600" testid="mgmt-tickets" />
              <StatCard label="Average ticket" value={money(d.avg_ticket)} icon={TrendingUp} tint="bg-emerald-50 text-emerald-600" testid="mgmt-avg-ticket" />
              <StatCard label="Online share" value={`${d.online_share}%`} icon={Globe2} tint="bg-indigo-50 text-indigo-600" testid="mgmt-online-share" />
              <StatCard label="Insurance collected" value={money(d.insurance)} icon={ShieldPlus} tint="bg-emerald-50 text-emerald-600" testid="mgmt-insurance" />
              <StatCard label="Refunds" value={money(d.refunds)} icon={Undo2} tint="bg-rose-50 text-rose-600" testid="mgmt-refunds" />
              <StatCard label="Fleet load factor" value={`${d.fleet.load_factor}%`} sub={`${d.fleet.departed} departed · ${d.fleet.cancelled} cancelled`} icon={BusFront} tint="bg-amber-50 text-amber-600" testid="mgmt-load-factor" />
              <StatCard label="Cash in vault" value={money(d.cash.vault)} sub={`${d.cash.pending_count} handovers pending · ${money(d.cash.pending_handover)}`} icon={Vault} tint="bg-sky-50 text-sky-600" testid="mgmt-vault" />
            </div>
            <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
              <div className="card-soft p-5" data-testid="mgmt-revenue-chart">
                <div className="label-xs mb-3">Daily revenue · online vs counter</div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.daily.map((x) => ({ ...x, day: fmtDate(x.date, { day: "numeric", month: "short" }) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={55} />
                      <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#EFF6FF" }} />
                      <Legend />
                      <Bar dataKey="online" name="Online" stackId="a" fill="#0052FF" />
                      <Bar dataKey="counter" name="Counter" stackId="a" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="card-soft p-5" data-testid="mgmt-method-chart">
                <div className="label-xs mb-3">Payment methods</div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={d.by_method} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                        {d.by_method.map((m, i) => <Cell key={m.name} fill={PIE[i]} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [money(v), METHOD_LABEL[n]]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {d.by_method.map((m, i) => (
                  <div key={m.name} className="flex justify-between text-sm"><span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full" style={{ background: PIE[i] }} />{METHOD_LABEL[m.name]}</span><b>{money(m.value)}</b></div>
                ))}
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <Table title="Revenue by route" rows={d.by_route} cols={[["tickets", (v) => `${v} tkts`], ["revenue", money]]} testid="mgmt-by-route" />
              <Table title="Revenue by station" rows={d.by_station} cols={[["tickets", (v) => `${v} tkts`], ["revenue", money]]} testid="mgmt-by-station" />
              <Table title="Teller performance" rows={d.by_teller} cols={[["tickets", (v) => `${v} tkts`], ["revenue", money]]} testid="mgmt-by-teller" />
            </div>
            <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
              <Table title="Revenue sharing from bus sign-outs" rows={d.shares} cols={[["disbursed", (v) => <span className="text-emerald-700">{money(v)}</span>], ["pending", (v) => <span className="text-amber-700">{money(v)}</span>]]} testid="mgmt-shares" />
              <div className="card-soft space-y-2 p-5 text-sm" data-testid="mgmt-cash-control">
                <div className="label-xs mb-1">Cash control</div>
                <div className="flex justify-between"><span>Awaiting handover</span><b>{money(d.cash.pending_handover)}</b></div>
                <div className="flex justify-between"><span>Banked this period</span><b>{money(d.cash.deposited)}</b></div>
                <div className="flex justify-between"><span>Handover variance</span><b className={d.cash.variance < 0 ? "text-rose-600" : "text-emerald-700"}>{money(d.cash.variance)}</b></div>
                <div className="flex justify-between"><span>Cash in vault</span><b>{money(d.cash.vault)}</b></div>
              </div>
            </div>
            <div className="card-soft p-5" data-testid="mgmt-ledger"><CashLedger /></div>
          </>
        )}
      </div>
    </div>
  );
}
